// Web-Audio playback for rhythm options.
// A rhythm is an array of onset durations expressed in crotchet-beats,
// e.g. [1, 0.5, 0.5, 2] → crotchet, quaver, quaver, minim.
// Tones use a synthesised piano-like timbre.

let sharedCtx: AudioContext | null = null;
let activeMaster: GainNode | null = null;
let pianoWave: PeriodicWave | null = null;

/** Pitch the rhythm is played back on (middle-register piano note). */
const BASE_FREQ = 261.63; // C4

function getCtx(): AudioContext {
  if (!sharedCtx) {
    const Ctor =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    sharedCtx = new Ctor();
  }
  return sharedCtx;
}

/** Piano-like harmonic spectrum turned into a reusable PeriodicWave. */
function getPianoWave(ctx: AudioContext): PeriodicWave {
  if (pianoWave) return pianoWave;
  // Amplitude of each harmonic (index 0 = DC, 1 = fundamental, …).
  const amps = [0, 1, 0.6, 0.4, 0.28, 0.2, 0.14, 0.1, 0.07, 0.05, 0.04, 0.03];
  const real = new Float32Array(amps.length);
  const imag = new Float32Array(amps.length);
  for (let i = 0; i < amps.length; i++) imag[i] = amps[i];
  pianoWave = ctx.createPeriodicWave(real, imag, { disableNormalization: false });
  return pianoWave;
}

/** Silence any rhythm currently playing. */
export function stopRhythm(): void {
  if (activeMaster) {
    try {
      activeMaster.disconnect();
    } catch {
      /* noop */
    }
    activeMaster = null;
  }
}

function scheduleTone(
  ctx: AudioContext,
  master: GainNode,
  start: number,
  length: number
): void {
  const osc = ctx.createOscillator();
  osc.setPeriodicWave(getPianoWave(ctx));
  osc.frequency.setValueAtTime(BASE_FREQ, start);

  // Lowpass that closes over the note so upper harmonics fade (string decay feel).
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(5200, start);
  lp.frequency.exponentialRampToValueAtTime(1100, start + length);

  // Percussive attack, then exponential decay like a struck string.
  const gain = ctx.createGain();
  const peak = 0.28;
  const end = start + length;
  const midPoint = start + Math.min(length * 0.5, 0.35);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.006);
  gain.gain.exponentialRampToValueAtTime(peak * 0.32, midPoint);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);

  osc.connect(lp);
  lp.connect(gain);
  gain.connect(master);
  osc.start(start);
  osc.stop(end + 0.05);
}

/** A short percussive metronome tick (accented on the downbeat). */
function scheduleClick(
  ctx: AudioContext,
  master: GainNode,
  start: number,
  accent: boolean
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(accent ? 2000 : 1350, start);

  const peak = accent ? 0.3 : 0.18;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.05);

  osc.connect(gain);
  gain.connect(master);
  osc.start(start);
  osc.stop(start + 0.06);
}

/** How many clicks make one count-in bar, and the gap between them (crotchet-beats). */
export interface CountIn {
  clicks: number;
  interval: number;
}

/**
 * Play a rhythm. Each onset is sounded as a piano-like tone whose audible
 * length tracks the note's duration so ties / long notes are clearly longer.
 * When `countIn` is given, one bar of metronome clicks precedes the rhythm so
 * the listener knows the tempo.
 */
export function playRhythm(onsets: number[], tempo = 100, countIn?: CountIn): void {
  const ctx = getCtx();
  stopRhythm();

  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(ctx.destination);
  activeMaster = master;

  // Schedule only once the context is actually running, so timing is read
  // against a live currentTime (a freshly-created context starts suspended).
  const startPlayback = () => {
    const beatDur = 60 / tempo; // seconds per crotchet beat
    let t = ctx.currentTime + 0.12;

    // One bar of metronome count-in (downbeat accented)
    if (countIn && countIn.clicks > 0) {
      for (let i = 0; i < countIn.clicks; i++) {
        scheduleClick(ctx, master, t, i === 0);
        t += countIn.interval * beatDur;
      }
    }

    // The rhythm itself, starting on the next downbeat
    onsets.forEach((beats) => {
      const dur = beats * beatDur;
      scheduleTone(ctx, master, t, Math.max(0.09, dur * 0.85));
      t += dur;
    });
  };

  if (ctx.state === "running") {
    startPlayback();
  } else {
    ctx.resume().then(startPlayback).catch(startPlayback);
  }
}

/** Decode an encoded rhythm string ("1,0.5,0.5,2") back to a number array. */
export function decodeRhythm(encoded: string): number[] {
  return encoded
    .split(",")
    .map((s) => parseFloat(s))
    .filter((n) => !Number.isNaN(n));
}
